import { forwardRef } from 'react';
import { Container as MuiContainer, ContainerProps as MuiContainerProps } from '@mui/material';

/**
 * Container component - A responsive layout container for centering content.
 *
 * This is a thin wrapper around Material-UI's Container component that provides:
 * - Centered content with horizontal padding (gutters)
 * - Responsive maxWidth breakpoints (xs, sm, md, lg, xl)
 * - Fluid container option (no max-width constraint)
 * - Fixed container option (max-width matches current breakpoint)
 * - Theme-aware styling via sx prop
 *
 * Based on analysis of mobile app usage (5 instances):
 * - 100% page-level layout (never nested in Containers)
 * - Two primary patterns: full-width responsive and fixed form layouts
 * - Centralized styling via sharedStyles
 *
 * @example
 * // Basic responsive container (default maxWidth="lg")
 * <Container maxWidth="lg">
 *   <Typography variant="h1">Page Title</Typography>
 *   <Content />
 * </Container>
 *
 * @example
 * // Full-width no padding (common page pattern)
 * <Container sx={responsiveContainerNoPaddingStyles}>
 *   <PageContent />
 * </Container>
 *
 * @example
 * // Fixed form container
 * <Container sx={formContainerStyles}>
 *   <Form />
 * </Container>
 *
 * @example
 * // Fluid container (no max-width)
 * <Container maxWidth={false}>
 *   <FullWidthContent />
 * </Container>
 *
 * @example
 * // Fixed container (max-width matches current breakpoint)
 * <Container fixed maxWidth="md">
 *   <Content />
 * </Container>
 *
 * @example
 * // Semantic main element
 * <Container component="main" maxWidth="lg">
 *   <MainContent />
 * </Container>
 */
export type ContainerProps = MuiContainerProps;

/**
 * Container component - Responsive content wrapper.
 *
 * A centered container that supports responsive maxWidth constraints.
 * This is a thin wrapper around MUI Container that preserves all Material-UI
 * capabilities while providing a consistent import path through the ui-library.
 */
export const Container = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => (
  <MuiContainer ref={ref} {...props} />
));

Container.displayName = 'Container';
